import React, { useState, useEffect, useCallback } from 'react';
import { Monitor } from '../../../types/monitoring';
import { apiClient } from '../../../lib/api';
import { MonitorList, MonitorDetailView } from './monitors';
import { useSettingsModal } from '../../../contexts/SettingsModalContext';

interface MonitorsSectionProps {
  initialMonitorId?: string | null;
}

export const MonitorsSection: React.FC<MonitorsSectionProps> = ({ initialMonitorId }) => {
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMonitor, setSelectedMonitor] = useState<Monitor | null>(null);
  const { clearInitialMonitorId } = useSettingsModal();

  const loadMonitors = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.listMonitors();
      setMonitors(response.monitors || []);
    } catch (error) {
      console.error('Error loading monitors:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMonitors();
  }, [loadMonitors]);

  useEffect(() => {
    if (initialMonitorId && monitors.length > 0) {
      const targetMonitor = monitors.find((m: Monitor) => m.id === initialMonitorId);
      if (targetMonitor) {
        setSelectedMonitor(targetMonitor);
        clearInitialMonitorId();
      }
    }
  }, [initialMonitorId, monitors, clearInitialMonitorId]);

  const handleSelectMonitor = (monitor: Monitor) => {
    setSelectedMonitor(monitor);
  };

  const handleBackToList = () => {
    setSelectedMonitor(null);
  };

  const handleMonitorUpdated = () => {
    loadMonitors();
  };

  if (selectedMonitor) {
    return (
      <MonitorDetailView
        monitor={selectedMonitor}
        onBack={handleBackToList}
        onMonitorUpdated={handleMonitorUpdated}
      />
    );
  }

  return (
    <MonitorList
      monitors={monitors}
      isLoading={isLoading}
      onSelectMonitor={handleSelectMonitor}
      onRefresh={loadMonitors}
    />
  );
};
